import type { BackofficeOperator } from '@repo/sdk/gen'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListToolbar } from '@repo/ui/components/list-toolbar'
import { PageLayout } from '@repo/ui/components/page-layout'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { OperatorBreadcrumb } from '@/routes/-components/operator-breadcrumb'
import { InviteOperatorAction } from '@/routes/(private)/operators/-components/invite-operator-action'
import { OperatorsEmpty } from '@/routes/(private)/operators/-components/operators-empty'
import { OperatorsFilters } from '@/routes/(private)/operators/-components/operators-filters'
import { OperatorsTable } from '@/routes/(private)/operators/-components/operators-table'
import { OPERATOR_STATUSES } from '@/routes/(private)/operators/-lib/operator-status'
import { operatorsListQueryOptions } from '@/routes/(private)/operators/-query/query-options'

const searchSchema = z.object({
	status: z.array(z.enum(OPERATOR_STATUSES)).optional(),
	search: z.string().trim().default('')
})

export const Route = createFileRoute('/(private)/operators/')({
	component: OperatorOperators,
	validateSearch: searchSchema,
	search: {
		middlewares: [stripSearchParams(searchSchema.parse({}))]
	},
	staticData: {
		breadcrumb: 'operator-operators:page_title'
	}
})

/**
 * The people who can sign in to this console: invitations still waiting, the
 * operators who arrived, and those barred. One list, because the operator
 * looking for somebody does not know in advance which of the three they are.
 *
 * Every act on a row is the server's to allow. The two guardrails it keeps —
 * no act on oneself, and never the last active operator — are refused there
 * and stated in the dialog that attempted them; the caller's own row offers
 * nothing in the first place.
 */
function OperatorOperators() {
	const { t } = useTranslation()
	const params = Route.useSearch()

	const operators = useQuery(
		operatorsListQueryOptions({
			search: params.search || undefined,
			status: params.status
		})
	)

	return (
		<PageLayout
			bar={
				<ListToolbar
					actions={<InviteOperatorAction />}
					search={
						<ListInputSearch
							placeholder={t(
								'operator-operators:search_placeholder'
							)}
						/>
					}
				>
					<OperatorsFilters />
				</ListToolbar>
			}
			breadcrumb={<OperatorBreadcrumb />}
		>
			<div className='flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6'>
				<OperatorsTable
					data={operators.data?.operators ?? EMPTY_OPERATORS}
					state={{
						empty: (
							<OperatorsEmpty
								filtered={
									params.search !== '' ||
									params.status !== undefined
								}
							/>
						),
						error: operators.isError,
						loading:
							operators.isPending || operators.isPlaceholderData
					}}
					total={operators.data?.total ?? 0}
				/>
			</div>
		</PageLayout>
	)
}

const EMPTY_OPERATORS: BackofficeOperator[] = []
